// A stock's Calendar tab (trades, assessments, leads, emails, documents, reviews, announcements and
// key dates on one timeline, with a month view) and its Docs tab (files for Claude to read).
import { api, supabase } from "./api";
import { askClaudeUrl } from "./connector";
import { date, dateTime, esc, sydneyDate } from "./format";

interface CalEvent { date: string; kind: string; title: string; detail?: string; link?: string; id?: number; tone?: string; editable?: boolean }

const KINDS: Record<string, string> = {
  key_date: "Key dates", trade: "Trades", assessment: "Assessments", catalyst: "Catalysts", promise: "Promises", review: "Reviews",
  lead: "Leads", email: "Emails", document: "Documents", announcement: "Announcements",
};
const DATE_KINDS = ["contract", "results", "agm", "dividend", "catalyst", "deadline", "other"];
const hidden = new Set<string>();
let shown: { code: string; month: string } | null = null;

const monthLabel = (ym: string) => new Date(`${ym}-01T00:00:00`).toLocaleDateString("en-AU", { month: "long", year: "numeric" });
const addMonths = (ym: string, n: number) => {
  const d = new Date(`${ym}-01T00:00:00`);
  d.setMonth(d.getMonth() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function monthGrid(ym: string, events: CalEvent[], today: string) {
  const first = new Date(`${ym}-01T00:00:00`);
  const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // Monday first
  const by = new Map<string, CalEvent[]>();
  for (const e of events) if (e.date.startsWith(ym)) by.set(e.date, [...(by.get(e.date) ?? []), e]);
  const cells: string[] = Array.from({ length: lead }, () => `<span class="cal-day empty"></span>`);
  for (let d = 1; d <= days; d++) {
    const iso = `${ym}-${String(d).padStart(2, "0")}`;
    const ev = by.get(iso) ?? [];
    const kinds = [...new Set(ev.map((e) => e.kind))].slice(0, 4);
    cells.push(`<button class="cal-day${iso === today ? " today" : ""}${ev.length ? " has" : ""}" data-day="${iso}" ${ev.length ? "" : "disabled"} title="${esc(ev.map((e) => e.title).join("\n"))}">
      <span>${d}</span><span class="dots">${kinds.map((k) => `<i class="dot k-${k}"></i>`).join("")}</span></button>`);
  }
  return `<div class="cal-grid">${["M", "T", "W", "T", "F", "S", "S"].map((w) => `<span class="cal-wd">${w}</span>`).join("")}${cells.join("")}</div>`;
}

function row(e: CalEvent, code: string) {
  const title = e.link ? `<a href="${esc(e.link)}"${e.link.startsWith("http") ? ' target="_blank" rel="noopener noreferrer"' : ""}>${esc(e.title)}</a>` : esc(e.title);
  return `<li class="ev k-${e.kind}" data-date="${e.date}"><span class="ev-date">${date(e.date)}</span>
    <span class="ev-body"><span class="pill k-${e.kind}">${esc(KINDS[e.kind] ?? e.kind).replace(/s$/, "")}</span> <span class="${e.tone ?? ""}">${title}</span>
    ${e.detail ? `<small class="muted">${esc(e.detail.length > 300 ? e.detail.slice(0, 300) + "…" : e.detail)}</small>` : ""}
    ${e.editable ? `<button class="link-btn" data-del-date="${e.id}" data-code="${esc(code)}">Remove</button>` : ""}</span></li>`;
}

export async function calendarPanel(code: string, el: HTMLElement): Promise<void> {
  const { events } = await api<{ events: CalEvent[] }>(`stock/${code}/calendar`);
  if (!location.hash.startsWith(`#/${code}/calendar`)) return; // moved on while loading
  const today = sydneyDate();
  if (shown?.code !== code) shown = { code, month: today.slice(0, 7) };
  const draw = () => {
    const vis = events.filter((e) => !hidden.has(e.kind));
    const upcoming = vis.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
    const past = vis.filter((e) => e.date < today);
    const kinds = [...new Set(events.map((e) => e.kind))];
    el.innerHTML = `
      <section class="card cal">
        <div class="cal-head"><button class="small-btn" data-m="-1" aria-label="Previous month">‹</button><b>${monthLabel(shown!.month)}</b><button class="small-btn" data-m="1" aria-label="Next month">›</button><button class="small-btn" data-m="0">Today</button></div>
        ${monthGrid(shown!.month, vis, today)}
        ${kinds.length ? `<div class="chips cal-filter">${kinds.map((k) => `<button class="chip k-${k}${hidden.has(k) ? " off" : ""}" data-kind="${k}"><i class="dot k-${k}"></i>${esc(KINDS[k] ?? k)}</button>`).join("")}</div>` : ""}
      </section>
      <section class="card"><h3>Coming up</h3>${upcoming.length ? `<ul class="events">${upcoming.map((e) => row(e, code)).join("")}</ul>` : `<p class="muted">Nothing dated yet. Claude adds key dates (contract decisions, results, deadlines) when it assesses or reviews ${esc(code)}, or add one below.</p>`}
        <details class="add-date"><summary>Add a date</summary>
          <form id="date-form" class="form-row">
            <input type="date" name="date" required><input name="title" placeholder="What's happening" maxlength="200" required>
            <select name="kind">${DATE_KINDS.map((k) => `<option>${k}</option>`).join("")}</select>
            <input name="detail" placeholder="Note (optional)" maxlength="500"><button class="primary small-btn">Add</button>
          </form></details></section>
      <section class="card"><h3>History</h3>${past.length ? `<ul class="events">${past.map((e) => row(e, code)).join("")}</ul>` : `<p class="muted">No history yet.</p>`}</section>`;
    el.querySelectorAll<HTMLButtonElement>("[data-m]").forEach((b) => b.addEventListener("click", () => {
      shown!.month = b.dataset.m === "0" ? today.slice(0, 7) : addMonths(shown!.month, Number(b.dataset.m));
      draw();
    }));
    el.querySelectorAll<HTMLButtonElement>("[data-kind]").forEach((b) => b.addEventListener("click", () => {
      const k = b.dataset.kind!;
      hidden.has(k) ? hidden.delete(k) : hidden.add(k);
      draw();
    }));
    el.querySelectorAll<HTMLButtonElement>("[data-day]").forEach((b) => b.addEventListener("click", () => {
      el.querySelector<HTMLElement>(`.ev[data-date="${b.dataset.day}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }));
    el.querySelectorAll<HTMLButtonElement>("[data-del-date]").forEach((b) => b.addEventListener("click", async () => {
      if (!confirm("Remove this date?")) return;
      await api(`stock/${code}/dates/${b.dataset.delDate}`, "DELETE").catch((e) => alert(e.message));
      calendarPanel(code, el);
    }));
    el.querySelector<HTMLFormElement>("#date-form")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const f = new FormData(e.target as HTMLFormElement);
      try {
        await api(`stock/${code}/dates`, "POST", Object.fromEntries(f));
        calendarPanel(code, el);
      } catch (err) {
        alert((err as Error).message);
      }
    });
  };
  draw();
}

interface Doc { id: number; name: string; type: string; size: number; note: string | null; uploadedAt: string; processedAt: string | null; processedBy: string | null; chars: number }

const sizeText = (b: number) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

export async function docsPanel(code: string, el: HTMLElement): Promise<void> {
  const { documents } = await api<{ documents: Doc[] }>(`stock/${code}/documents`);
  if (!location.hash.startsWith(`#/${code}/docs`)) return; // moved on while loading
  const waiting = documents.filter((d) => !d.processedAt).length;
  el.innerHTML = `
    <section class="card">
      <h3>Documents for Claude</h3>
      <p class="muted small">Upload reports, broker notes, contracts or anything about ${esc(code)}. Claude reads new ones in full the next time it assesses ${esc(code)} or reviews a group that includes it, and adds any dates it finds to the Calendar. PDFs and text files can be read; scanned PDFs and images can't (yet).</p>
      <form id="doc-form" class="form-row">
        <input type="file" name="file" required accept=".pdf,.txt,.csv,.md,.html,.htm,.json,application/pdf,text/*">
        <input name="note" placeholder="Note for Claude (optional)" maxlength="500">
        <button class="primary small-btn">Upload</button>
      </form>
      <p class="muted small" id="doc-status"></p>
      ${waiting ? `<p>${waiting} waiting for Claude. <a class="ask" href="${esc(askClaudeUrl(code))}" target="_blank" rel="noopener">Ask Claude to Assess ${esc(code)}</a></p>` : ""}
    </section>
    <section class="card">${documents.length ? `<ul class="plain docs">${documents.map((d) => `<li>
      <b>${esc(d.name)}</b> <span class="muted small">${sizeText(d.size)} · uploaded ${dateTime(d.uploadedAt)}</span>
      ${d.processedBy === "nothing to read" ? `<span class="pill warn">Claude can't read this</span>` : d.processedAt ? `<span class="pill">Read in ${esc(d.processedBy ?? "an analysis")}</span>` : `<span class="pill new">Waiting for Claude</span>`}
      ${d.note ? `<small class="muted">Your note: ${esc(d.note)}</small>` : ""}
      <span class="actions"><button class="small-btn" data-open="${d.id}">Open</button>${d.processedAt && d.chars ? `<button class="small-btn" data-again="${d.id}">Read again next time</button>` : ""}<button class="link-btn" data-del="${d.id}">Delete</button></span>
    </li>`).join("")}</ul>` : `<p class="muted">No documents yet.</p>`}</section>`;

  const status = el.querySelector<HTMLElement>("#doc-status")!;
  el.querySelector<HTMLFormElement>("#doc-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const file = (form.elements.namedItem("file") as HTMLInputElement).files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { status.textContent = "Files can be up to 20 MB."; return; }
    status.textContent = `Uploading ${file.name}…`;
    const { data } = await supabase.auth.getSession();
    const r = await fetch(`/api/stock/${code}/documents`, {
      method: "POST", body: file,
      headers: {
        Authorization: `Bearer ${data.session?.access_token ?? ""}`, "content-type": file.type || "application/octet-stream",
        "x-file-name": encodeURIComponent(file.name), "x-note": encodeURIComponent((form.elements.namedItem("note") as HTMLInputElement).value),
      },
    });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) { status.textContent = out.error ?? "Upload failed."; return; }
    await docsPanel(code, el);
    el.querySelector<HTMLElement>("#doc-status")!.textContent = out.document.problem ?? `Uploaded. Gold read ${out.document.chars.toLocaleString("en-AU")} characters of text for Claude.`;
  });
  el.querySelectorAll<HTMLButtonElement>("[data-open]").forEach((b) => b.addEventListener("click", async () => {
    const { data } = await supabase.auth.getSession();
    const w = window.open("", "_blank");
    const r = await fetch(`/api/stock/${code}/documents/${b.dataset.open}/file`, { headers: { Authorization: `Bearer ${data.session?.access_token ?? ""}` } });
    if (!r.ok) { w?.close(); alert("That document couldn't be opened."); return; }
    const url = URL.createObjectURL(await r.blob());
    if (w) w.location.href = url; else location.href = url;
  }));
  el.querySelectorAll<HTMLButtonElement>("[data-again]").forEach((b) => b.addEventListener("click", async () => {
    await api(`stock/${code}/documents/${b.dataset.again}/unread`, "POST").catch(() => {});
    docsPanel(code, el);
  }));
  el.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((b) => b.addEventListener("click", async () => {
    if (!confirm("Delete this document from Gold?")) return;
    await api(`stock/${code}/documents/${b.dataset.del}`, "DELETE").catch(() => {});
    docsPanel(code, el);
  }));
}
